import { attributionTokenFields } from "@/lib/attribution-token";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./auth-page-layout";
import { AuthTermsNotice } from "./auth-terms-notice";
import { GoogleSignInButton } from "./google-sign-in-button";
import { useAuth, getAuthErrorMessage } from "@/hooks/use-auth";
import { SignupChoiceFields } from "@/components/signup/signup-choice-fields";
import {
  clearSignupAttribution,
  signupAttributionFields,
} from "@/lib/signup-attribution";
import { NO_SIGNUP_CHOICES, signupChoiceFields } from "@/lib/signup-choices";
import {
  resolveDesktopFirstNext,
  stashPendingNext,
  withNext,
} from "./next-redirect";
import {
  Button,
  FormError,
  FormField,
  PasswordInput,
  TextInput,
} from "@/components/ui";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";

export function RegisterPage() {
  const t = useT(authMessages);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = resolveDesktopFirstNext(searchParams.get("next"));
  const loginPath = withNext("/auth/login", nextUrl);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [choices, setChoices] = useState(NO_SIGNUP_CHOICES);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password1 !== password2) {
      setError(t("passwordsDoNotMatch"));
      return;
    }

    setIsSubmitting(true);
    try {
      await register({
        name,
        email,
        password1,
        password2,
        ...signupChoiceFields(choices),
        ...signupAttributionFields(),
        ...attributionTokenFields(),
      });
      clearSignupAttribution();
      // The verify-email link arrives in a separate browser tab, so React
      // Router state alone can't carry `next` across the gap. Persist it.
      stashPendingNext(nextUrl);
      navigate(withNext("/auth/register-success", nextUrl), {
        state: { email, next: nextUrl },
      });
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPageLayout landingLink title={t("createYourAccount")}>
      <div className="auth-form-page">
        <FormError
          message={error}
          className="mt-0"
          data-testid="register-error"
        />

        {/* mx-2 matches the form's 8px horizontal inset so every control
            on the card shares one width. */}
        <div className="mx-2">
          <GoogleSignInButton
            label={t("signUpWithGmail")}
            next={nextUrl}
            signupChoices={choices}
            data-testid="register-google-button"
          />
        </div>

        <div className="auth-divider mx-2" aria-hidden="true">
          <span>{t("orDivider")}</span>
        </div>

        <form
          onSubmit={handleSubmit}
          className="auth-login-form flex flex-col"
          data-testid="register-form"
        >
          <FormField label={t("name")} htmlFor="name">
            <TextInput
              id="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("name")}
              className="auth-liquid-input h-12 rounded-[18px] px-4 text-appkit-subhead"
              data-testid="register-name"
            />
          </FormField>

          <FormField label={t("email")} htmlFor="email">
            <TextInput
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailPlaceholder")}
              className="auth-liquid-input h-12 rounded-[18px] px-4 text-appkit-subhead"
              data-testid="register-email"
            />
          </FormField>

          <FormField label={t("password")} htmlFor="password1">
            <PasswordInput
              id="password1"
              required
              autoComplete="new-password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
              placeholder={t("password")}
              className="auth-liquid-input h-12 rounded-[18px] text-appkit-subhead"
              data-testid="register-password1"
            />
          </FormField>

          <FormField label={t("confirm")} htmlFor="password2">
            <PasswordInput
              id="password2"
              required
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              onBlur={() => setPasswordTouched(true)}
              placeholder={t("confirm")}
              className="auth-liquid-input h-12 rounded-[18px] text-appkit-subhead"
              data-testid="register-password2"
            />
            {passwordTouched && password2 && password1 !== password2 && (
              <p
                className="mt-1.5 text-appkit-caption text-danger"
                data-testid="password-mismatch-error"
              >
                {t("passwordsDoNotMatch")}
              </p>
            )}
          </FormField>

          <Button
            type="submit"
            variant="secondary"
            size="lg"
            disabled={isSubmitting}
            className="auth-liquid-primary-button w-full font-semibold"
            data-testid="register-submit"
          >
            {isSubmitting ? t("creatingAccount") : t("createAccount")}
          </Button>
        </form>

        <div className="auth-choice-footer">
          <SignupChoiceFields
            choices={choices}
            onChange={setChoices}
            prefix="register"
            variant="auth"
          />
          <AuthTermsNotice action="create" />
          <Link
            to={loginPath}
            viewTransition
            className="auth-small-link auth-switch-link"
            data-testid="login-link"
          >
            {t("signIn")}
          </Link>
        </div>
      </div>
    </AuthPageLayout>
  );
}
